let captureFw;

// Converts blob to image dataUrl to display
const convertBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

async function blobToDataUrl(file) {
  const dataURL = await convertBase64(file);
  dataURL.replace(",", "---");
  return dataURL;
}

// Converts blob to file
async function blobtoFile(blob, filename, type = "images/png") {
  const dataUrl = await blobToDataUrl(blob);
  return {
    dataUrl,
    name: filename,
    original: new File([blob], filename, { type }),
    type,
  };
}

const convertResultToBlob = (res) => {
  const dataType = res.split(";base64,")[0].split(":")[1];
  const base64Result = res.split(";base64,")[1];

  const base64ByteCharacters = atob(base64Result);
  const base64ByteNumbers = new Array(base64ByteCharacters.length);
  for (let i = 0; i < base64ByteNumbers.length; i += 1) {
    base64ByteNumbers[i] = base64ByteCharacters.charCodeAt(i);
  }
  const base64ByteArray = new Uint8Array(base64ByteNumbers);

  return new Blob([base64ByteArray], { type: dataType });
};

const setSelfieSettings = (settings) => {
  const selfieSettings = settings;
  selfieSettings.showOnlyOneFace = "Make sure only one face is on the screen.";
  selfieSettings.somethingWentWrong =
    "We encountered an issue. Please try again.";
  selfieSettings.error = "We encountered an issue. Please try again.";
  selfieSettings.versionNotSupported =
    "Browser version is not supported. Please try again.";
  selfieSettings.updateBrowser =
    "Update your browser version. Please try again.";
  selfieSettings.tryAgain = "We encountered an issue. Please try again.";
  selfieSettings.noCameraAvailable = "Unable to access the camera.";
  selfieSettings.checkCameraConnection = "Unable to access the camera.";
  selfieSettings.lookStraight = "Look straight while performing the capture.";
  selfieSettings.fitYourFace = "Move your face closer to the screen.";
  selfieSettings.moveCloser = "Move your face closer to the screen.";
  selfieSettings.moveAway = "Move your face farther from the screen.";
  selfieSettings.holdSteady = "Keep still while performing the capture.";
  selfieSettings.processing = "Processing";
  selfieSettings.noCameraPermission = "Unable to access the camera.";
  selfieSettings.showSelfieCaptureHeader = false;
  selfieSettings.companyLogo = process.env.REACT_APP_AID_LOGO;
  selfieSettings.showSelfieHelpButton = true;
};

const setDocumentSettings = (settings) => {
  const documentSettings = settings;
  documentSettings.error = "We encountered an issue. Please try again.";
  documentSettings.somethingWentWrong =
    "We encountered an issue. Please try again.";
  documentSettings.tryAgain = "We encountered an issue. Please try again.";
  documentSettings.cameraUnavailable = "Unable to access the camera.";
  documentSettings.noCameraAvailable = "Unable to access the camera.";
  documentSettings.cameraConnection = "Unable to access the camera.";
  documentSettings.versionNotSupported =
    "Browser version is not supported. Please try again.";
  documentSettings.updateBrowser =
    "Update your browser version. Please try again.";
  documentSettings.processing = "Processing Image";
  documentSettings.detectingDocument = "Detecting document";
  documentSettings.placeDocumentIntoFrame =
    "Align the document within the frame.";
  documentSettings.noFocus = "Please focus your camera";
  documentSettings.moveCloser = "Move document closer.";
  documentSettings.glaresOnDocument = "Avoid glare on the document.";
  documentSettings.companyLogo = process.env.REACT_APP_AID_LOGO;
};

export function initializeAID() {
  if (document.getElementById("aid-script")) {
    return;
  }

  const script = document.createElement("script");
  script.src =
    "/libs/authentic-id/js/IDMetricsCaptureFramework-5.1.1-plaincode.5631e722c3ff2a11098b.js";
  script.id = "aid-script";
  script.async = false;
  script.onload = () => {
    window.onCaptureFrameworkLoaded = async function () {
      captureFw = this.IDMetricsCaptureFramework;

      const detectionProvider = 1;

      const languageConfig = {
        lang: "en",
        localizedStrings: {
          notifications: {
            faceNotFound: "Face not aligned",
            tooManyFaces: "More than one face",
            moveAway: "Move away",
            moveCloser: "Move closer",
            nonBlurry: "Keep your phone steady to capture a clear image",
            onDark: "Please move to a brighter location or turn on more lights",
            onFaceAngleView: "Align face within the white silhouette",
            removeLenses: "Take off your glasses",
            lenses: "Lenses detected",
            eyesClosed: "Eyes closed",
            mask: "Face mask detected",
            centerFaceV2: "Make sure you align the face within the silhouette",
            getReady: "Get ready and stay still...",
          },
        },
      };

      await captureFw.setDetectionProvider(detectionProvider, languageConfig);

      window.rootResourcePath = `${process.env.REACT_APP_AID_PROC_MGR_URL}/`;
    };

    window.onCaptureFrameworkLoadFailed = function () {
      console.log("AID Capture Framework Load Failed");
    };
  };

  document.body.appendChild(script);
}

// Store result of aidCapture event in fileList
async function storeImage(images) {
  // //check if promised result is empty
  // if (images == null) {
  //   return;
  // }
  const fileList = [];
  for (let i = 0; i <= images.length - 1; i += 1) {
    const image = images[i];
    const filename = `mwc-${new Date().getTime()}.jpg`;
    const filePromise = blobtoFile(image, filename);
    fileList[i] = filePromise;
  }
  return Promise.all(fileList);
}

// Invokes aidCapture and returns result as array of fileList
export default async function useAIDCapture(event, settings = null) {
  return new Promise((resolve, reject) => {
    document.body.classList.add("is-aid-capture-active");
    let images;
    const captureResult = new window.CaptureResult();

    captureResult.setOnFinish(async function () {
      // Camera is done → back to idle
      document.body.classList.remove("is-aid-capture-active");

      if (captureResult.isSuccessful) {
        if (event === "SELFIE" || event === "PASSPORT") {
          const result = convertResultToBlob(captureResult.result);
          images = await storeImage([result]);
        } else {
          let frontImage;
          let backImage;
          //result is available when not cropped and croppedResult is available when cropped.  in manual mode.
          if (captureResult.croppedResult) {
            frontImage = captureResult.croppedResult.front;
            backImage = captureResult.croppedResult.back;
          } else {
            frontImage = captureResult.result.front;
            backImage = captureResult.result.back;
          }
          const results = [];
          [frontImage, backImage].forEach((image) =>
            results.push(convertResultToBlob(image))
          );
          images = await storeImage(results);
        }
        resolve(images);
      }
    });

    captureResult.setOnAborted(function (abortEvent) {
      document.body.classList.remove("is-aid-capture-active");

      if (abortEvent.errorType === "user-cancel") {
        resolve();
      }
    });

    captureResult.setOnEvent(function (errorCode, errorDescription, event) {
      document.body.classList.remove("is-aid-capture-active");

      console.log("Error Code:", errorCode);
      console.log("Error Description:", errorDescription);
      console.log(event);
      reject(errorCode);
    });

    if (event === "SELFIE") {
      setSelfieSettings(settings);
      captureFw.scanSelfie(settings, captureResult);
    } else {
      setDocumentSettings(settings);
      captureFw.scanDocument(settings, captureResult);
    }
  });
}
